import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";

// Alias mapping for legacy or module permission keys to exact API permission keys
const PERMISSION_ALIASES = {
  manage_users: ["view_users", "edit_users", "add_users", "delete_users"],
  subadmins: ["view_sub_admin", "add_sub_admin", "edit_sub_admin", "delete_sub_admin", "exports_sub_admin"],
  coin_list: ["view_coin_list", "edit_coin_list"],
  statistics: ["view_statistics", "exports_statistics"],
  game_history: ["view_game_history", "exports_game_history"],
  jackpot_history: ["view_jackpot_history", "exports_jackpot_history"],
  weekly_reward_history: ["view_reward_history"],
  leaderboard: ["view_leaderboard", "edit_leaderboard", "exports_leaderboard"],
  claim_history: ["view_claim_history", "exports_claim_history"],
  logs: ["view_logs"],
  tasks: ["view_tasks", "add_tasks", "edit_tasks", "delete_tasks"],
  add_task: ["add_tasks"],
  edit_task: ["edit_tasks"],
  delete_task: ["delete_tasks"],
  roadmap: ["view_roadmap", "add_roadmap", "edit_roadmap", "delete_roadmap"],
  energy_package: ["view_energy_package", "add_energy_package", "edit_energy_package", "delete_energy_package"],
  payment_setting: ["view_payment_setting", "edit_payment_setting"],
  buy_energy_setting: ["view_buy_energy_setting", "edit_buy_energy_setting"],
  energy_purchase_history: ["view_energy_purchase_history"],
  reward_system_settings: ["view_reward_system_setting", "edit_reward_system_setting"],
  jackpot_settings: ["view_jackpot_setting", "edit_jackpot_setting"],
  edit_jackpot_settings: ["edit_jackpot_setting"],
  edit_energy_settings: ["edit_energy_setting"],
  energy_settings: ["view_energy_setting", "edit_energy_setting"],
  game_settings: ["view_game_setting", "edit_game_setting"],
  edit_game_settings: ["edit_game_setting"],
  leaderboard_settings: ["view_leaderboard_setting", "edit_leaderboard_setting"],
  edit_leaderboard_settings: ["edit_leaderboard_setting"],
  maintenance_settings: ["view_maintenance_setting", "edit_maintenance_setting"],
  edit_maintenance_settings: ["edit_maintenance_setting"],
  welcome_bonus_settings: ["view_verification_bonus_setting", "edit_verification_bonus_setting"],
  edit_welcome_bonus_settings: ["edit_verification_bonus_setting"],
  crypto_exchange_settings: ["view_crypto_exchange_setting", "edit_crypto_exchange_setting"],
  edit_crypto_exchange_settings: ["edit_crypto_exchange_setting"],
  conversion_settings: ["view_crypto_conversion_setting", "edit_crypto_conversion_setting"],
  edit_conversion_settings: ["edit_crypto_conversion_setting"],
  promotion_settings: ["view_promotion_setting", "edit_promotion_setting"],
  edit_promotion_settings: ["edit_promotion_setting"],
  crypto_deposit_settings: ["view_crypto_deposit_setting", "edit_crypto_deposit_setting"],
  edit_crypto_deposit_settings: ["edit_crypto_deposit_setting"],
  language_settings: ["view_language", "add_language", "edit_language", "delete_language"],
  edit_language_settings: ["edit_language"],
  historical_data_setting: ["view_historical_data_setting"],
  edit_historical_data_setting: ["view_historical_data_setting"],
  move_data_mySql: ["view_mysql_backup_data"],
  remove_data_mongoDb: ["view_delete_data_from_monogo"],
  round_multiplier: ["view_round_multiplier", "add_round_multiplier", "edit_round_multiplier", "delete_round_multiplier"],
  system_announcement: ["view_system_announcement", "add_system_announcement", "edit_system_announcement", "delete_system_announcement"],
  export_request: ["view_export_request", "add_export_request", "edit_export_request", "delete_export_request"],
  coin_history: ["view_coin_history"],
  update_user_balance: ["edit_users", "view_users"]
};

/**
 * Pure evaluation function for permission matching.
 */
export const evaluatePermission = (user, requiredPermissions, mode = "any") => {
  if (!user) return false;
  if (user?.role === "SUPERADMIN") return true;

  if (!requiredPermissions || (Array.isArray(requiredPermissions) && requiredPermissions.length === 0)) {
    return true;
  }

  const userPermsList = user?.permissions || [];
  const userPermSet = new Set([...userPermsList, "profile", "view_profile"]);

  const keys = Array.isArray(requiredPermissions)
    ? requiredPermissions
    : [requiredPermissions];

  const checkSingleKey = (key) => {
    if (!key) return true;
    if (userPermSet.has(key)) return true;

    // Check alias mappings
    const aliases = PERMISSION_ALIASES[key];
    if (aliases && aliases.some((alias) => userPermSet.has(alias))) {
      return true;
    }

    return false;
  };

  if (mode === "all") {
    return keys.every(checkSingleKey);
  }
  // Default: mode === "any"
  return keys.some(checkSingleKey);
};

/**
 * Custom hook to check permissions in React components.
 * Usage:
 *   const { hasPermission, isSuperAdmin } = usePermission();
 *   if (hasPermission("edit_users")) { ... }
 *   Or directly:
 *   const canEdit = usePermission("edit_users");
 */
export const usePermission = (requiredPermissions, mode = "any") => {
  const { user } = useSelector((state) => state.Auth);

  const isSuperAdmin = user?.role === "SUPERADMIN";

  const hasPermission = useCallback(
    (permissions, matchMode = "any") => {
      return evaluatePermission(user, permissions, matchMode);
    },
    [user?.role, user?.permissions]
  );

  const isAllowed = useMemo(
    () => (requiredPermissions ? evaluatePermission(user, requiredPermissions, mode) : true),
    [user?.role, user?.permissions, requiredPermissions, mode]
  );

  return {
    isAllowed,
    hasPermission,
    isSuperAdmin,
    userPermissions: user?.permissions || [],
  };
};

export default usePermission;
